import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const api = axios.create({ baseURL: API_BASE, headers: { 'Content-Type': 'application/json' } });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('mmarketing_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('mmarketing_token');
      localStorage.removeItem('mmarketing_user');
    }
    return Promise.reject(error);
  }
);

let ws = null;
let wsListeners = [];

export const connectWebSocket = (onMessage) => {
  if (ws && ws.readyState === WebSocket.OPEN) { wsListeners.push(onMessage); return; }
  ws = new WebSocket(`${WS_URL}/api/ws`);
  ws.onopen = () => { setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' })); }, 30000); };
  ws.onmessage = (event) => { const data = JSON.parse(event.data); wsListeners.forEach(l => l(data)); };
  ws.onclose = () => { setTimeout(() => connectWebSocket(onMessage), 3000); };
  wsListeners.push(onMessage);
};

export const disconnectWebSocket = (listener) => { wsListeners = wsListeners.filter(l => l !== listener); };

export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return false;
  return (await Notification.requestPermission()) === 'granted';
};

export const showNotification = (title, options = {}) => {
  if (Notification.permission === 'granted') new Notification(title, { icon: '/logo192.png', ...options });
};

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

// Products
export const productsAPI = {
  getAll: (category) => api.get('/products', { params: { category } }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  getVendorProducts: () => api.get('/vendor/products'),
};

// Flash Deals
export const flashDealsAPI = {
  getAll: (activeOnly = true) => api.get('/flash-deals', { params: { active_only: activeOnly } }),
  getById: (id) => api.get(`/flash-deals/${id}`),
  create: (data) => api.post('/flash-deals', data),
  claim: (id) => api.post(`/flash-deals/${id}/claim`),
  aiCreate: (productId) => api.post('/flash-deals/ai-create', null, { params: { product_id: productId } }),
};

// Services
export const servicesAPI = {
  getAll: (category) => api.get('/services', { params: { category } }),
  getById: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  book: (id) => api.post(`/services/${id}/book`),
  getSlots: (serviceId, date) => api.get(`/services/${serviceId}/slots`, { params: { date } }),
};

// Bookings
export const bookingsAPI = {
  create: (data) => api.post('/bookings', data),
  getAll: () => api.get('/bookings'),
  getVendorBookings: () => api.get('/vendor/bookings'),
  cancel: (id) => api.put(`/bookings/${id}/cancel`),
  complete: (id) => api.put(`/bookings/${id}/complete`),
};

// Reviews
export const reviewsAPI = {
  getForProduct: (id) => api.get(`/reviews/product/${id}`),
  getForService: (id) => api.get(`/reviews/service/${id}`),
  create: (data) => api.post('/reviews', data),
};

// Orders
export const ordersAPI = {
  create: (data) => api.post('/orders', data),
  getAll: () => api.get('/orders'),
  getById: (id) => api.get(`/orders/${id}`),
  cancel: (id) => api.put(`/orders/${id}/cancel`),
  getVendorOrders: (status) => api.get('/vendor/orders', { params: { status } }),
  updateStatus: (id, status) => api.put(`/vendor/orders/${id}/status`, null, { params: { status } }),
};

// Wallet
export const walletAPI = {
  getBalance: () => api.get('/wallet/balance'),
  getTransactions: () => api.get('/wallet/transactions'),
  addFunds: (amount) => api.post('/wallet/add-funds', null, { params: { amount } }),
  payOrder: (orderId) => api.post(`/wallet/pay/${orderId}`),
  withdraw: (amount) => api.post('/wallet/withdraw', { amount }),
  getWithdrawals: () => api.get('/wallet/withdrawals'),
};

// Referral
export const referralAPI = { getStats: () => api.get('/referral/stats') };

// Notifications
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  subscribePush: (subscription) => api.post('/notifications/subscribe', subscription),
};

// Payments
export const paymentsAPI = {
  createCheckout: (orderId) => api.post('/payments/checkout', null, { params: { order_id: orderId } }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Analytics
export const analyticsAPI = { getVendorAnalytics: () => api.get('/analytics/vendor') };

// AI
export const aiAPI = {
  getRecommendations: (limit = 5) => api.post('/ai/recommendations', null, { params: { limit } }),
  generateMarketingCopy: (name, desc) => api.post('/ai/marketing-copy', null, { params: { product_name: name, product_description: desc } }),
  smartSearch: (query) => api.post('/ai/smart-search', null, { params: { query } }),
  inventoryIntelligence: () => api.post('/ai/inventory-intelligence'),
  dynamicPricing: (productId) => api.post(`/ai/dynamic-pricing/${productId}`),
  triggerAutonomous: () => api.post('/ai/trigger-autonomous'),
  chatbot: (message, sessionId = 'default') => api.post('/ai/chatbot', null, { params: { message, session_id: sessionId } }),
};

// Email
export const emailAPI = {
  getSettings: () => api.get('/email/settings'),
  updateSettings: (enabled) => api.put('/email/settings', null, { params: { enabled } }),
};

// Settings
export const settingsAPI = {
  getNotificationPrefs: () => api.get('/settings/notifications'),
  updateNotificationPrefs: (prefs) => api.put('/settings/notifications', prefs),
  updateLocation: (lat, lng) => api.put('/settings/location', null, { params: { lat, lng } }),
  getNearbyDeals: (lat, lng) => api.get('/deals/nearby', { params: { lat, lng } }),
};

// VIP
export const vipAPI = {
  getTiers: () => api.get('/vip/tiers'),
  getStatus: () => api.get('/vip/status'),
};

// BNPL
export const bnplAPI = {
  apply: (orderId, installments = 4) => api.post('/bnpl/apply', null, { params: { order_id: orderId, installments } }),
  getPlans: () => api.get('/bnpl/plans'),
};

// Vendor
export const vendorAPI = {
  getPlans: () => api.get('/vendor/plans'),
  getSubscription: () => api.get('/vendor/subscription'),
  subscribe: (plan) => api.post('/vendor/subscribe', null, { params: { plan } }),
};

// Delivery
export const deliveryAPI = {
  estimate: (dropoff_address) => api.post('/delivery/estimate', { dropoff_address }),
  setup: () => api.post('/delivery/setup'),
  ship: (orderId, pickupAddress, dropoffAddress) => api.post(`/delivery/ship/${orderId}`, null, { params: { pickup_address: pickupAddress, dropoff_address: dropoffAddress } }),
  track: (orderId) => api.get(`/delivery/track/${orderId}`),
  list: () => api.get('/delivery/list'),
  cancel: (deliveryId) => api.post(`/delivery/cancel/${deliveryId}`),
};

// Public
export const publicAPI = {
  getCategories: () => api.get('/categories'),
  getStats: () => api.get('/stats'),
};

// Admin
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getDealHistory: () => api.get('/admin/deal-history'),
};

// Deal Rooms
export const roomsAPI = {
  create: (data) => api.post('/rooms', data),
  list: () => api.get('/rooms'),
  get: (id) => api.get(`/rooms/${id}`),
  propose: (id, data) => api.post(`/rooms/${id}/propose`, data),
  accept: (id) => api.post(`/rooms/${id}/accept`),
  close: (id) => api.post(`/rooms/${id}/close`),
  aiAssist: (id, context) => api.post(`/rooms/${id}/ai-assist`, { context }),
};

// Creator
export const creatorAPI = {
  createRoom: (data) => api.post('/creator/rooms', data),
  listRooms: (params) => api.get('/creator/rooms', { params }),
  myRooms: () => api.get('/creator/rooms/mine'),
  getRoom: (id) => api.get(`/creator/rooms/${id}`),
  subscribe: (id) => api.post(`/creator/rooms/${id}/subscribe`),
  tip: (id, amount) => api.post(`/creator/rooms/${id}/tip`, { amount }),
  goLive: (id) => api.post(`/creator/rooms/${id}/go-live`),
  checkAccess: (id) => api.get(`/creator/rooms/${id}/check-access`),
  getTurnConfig: () => api.get('/creator/turn-config'),
  // Profile
  updateProfile: (data) => api.put('/creator/profile', data),
  getProfile: (userId) => api.get(`/creator/profile/${userId}`),
  discover: (params) => api.get('/creator/discover', { params }),
  // Posts
  createPost: (data) => api.post('/creator/posts', data),
  getPosts: (creatorId) => api.get(`/creator/posts/${creatorId}`),
  unlockPost: (postId) => api.post(`/creator/posts/${postId}/unlock`),
  likePost: (postId) => api.post(`/creator/posts/${postId}/like`),
  teaserStatus: (postId) => api.get(`/creator/posts/${postId}/teaser-status`),
  // PPV Messages
  sendPPV: (data) => api.post('/creator/messages/ppv', data),
  getMessages: (userId) => api.get(`/creator/messages/${userId}`),
  unlockMessage: (msgId) => api.post(`/creator/messages/${msgId}/unlock`),
  // Analytics
  analytics: () => api.get('/creator/analytics'),
  // Referrals
  getReferral: () => api.get('/creator/referral'),
  // Tip Menu
  setTipMenu: (roomId, items) => api.put(`/creator/rooms/${roomId}/tip-menu`, { items }),
  getTipMenu: (roomId) => api.get(`/creator/rooms/${roomId}/tip-menu`),
  // Goal Meter
  setGoal: (roomId, goalAmount, goalLabel) => api.put(`/creator/rooms/${roomId}/goal`, { goal_amount: goalAmount, goal_label: goalLabel }),
  getGoal: (roomId) => api.get(`/creator/rooms/${roomId}/goal`),
  // Tip Race
  startRace: (roomId, targetAmount, prizeDescription) => api.post(`/creator/rooms/${roomId}/race/start`, { target_amount: targetAmount, prize_description: prizeDescription }),
  getActiveRace: (roomId) => api.get(`/creator/rooms/${roomId}/race`),
  raceTip: (roomId, amount) => api.post(`/creator/rooms/${roomId}/race/tip`, { amount }),
  // Exploding Media
  sendExploding: (data) => api.post('/creator/exploding-media', data),
  getExplodingInbox: () => api.get('/creator/exploding-media/inbox'),
  viewExploding: (mediaId) => api.post(`/creator/exploding-media/${mediaId}/view`),
  expireExploding: (mediaId) => api.post(`/creator/exploding-media/${mediaId}/expire`),
};

// Video (Sora 2)
export const videoAPI = {
  generate: (data) => api.post('/video/generate', data),
  status: (jobId) => api.get(`/video/status/${jobId}`),
  fileUrl: (jobId) => `${API_BASE}/video/file/${jobId}`,
  productVideo: (productId) => api.get(`/video/product/${productId}`),
  myVideos: () => api.get('/video/my-videos'),
  latestPromo: () => api.get('/video/latest-promo'),
};

// Story Videos (multi-clip + narration)
export const storyAPI = {
  generate: (data) => api.post('/story/generate', data),
  status: (jobId) => api.get(`/story/status/${jobId}`),
  fileUrl: (jobId) => `${API_BASE}/story/file/${jobId}`,
  productStory: (productId) => api.get(`/story/product/${productId}`),
  latestPromo: () => api.get('/story/latest-promo'),
};

// Media Upload
export const mediaAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/media/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  fileUrl: (path) => `${API_BASE}/media/files/${path}`,
};

// Growth Engine
export const growthAPI = {
  trendingProducts: (limit = 12) => api.get('/growth/trending/products', { params: { limit } }),
  trendingCreators: (limit = 12) => api.get('/growth/trending/creators', { params: { limit } }),
  trackShare: (platform, contentType, contentId) => api.post('/growth/share/track', null, { params: { platform, content_type: contentType, content_id: contentId } }),
  shareStats: () => api.get('/growth/share/stats'),
};

// Moderation
export const moderationAPI = {
  report: (data) => api.post('/moderation/report', data),
  queue: (status = 'pending') => api.get('/moderation/queue', { params: { status } }),
  stats: () => api.get('/moderation/stats'),
  action: (contentType, contentId, data) => api.post(`/moderation/action/${contentType}/${contentId}`, data),
};

// Projects (Funding)
export const projectsAPI = {
  create: (data) => api.post('/projects', data),
  list: (params) => api.get('/projects', { params }),
  get: (id) => api.get(`/projects/${id}`),
  invest: (id, data) => api.post(`/projects/${id}/invest`, data),
  portfolio: () => api.get('/projects/investor/portfolio'),
  roi: (id, amount) => api.get(`/projects/${id}/roi`, { params: { amount } }),
  launchBundle: (data) => api.post('/projects/launch-bundle', data),
  risingStars: (limit = 6) => api.get('/projects/featured/rising-stars', { params: { limit } }),
  createUpdate: (id, data) => api.post(`/projects/${id}/updates`, data),
  getUpdates: (id) => api.get(`/projects/${id}/updates`),
  getTrust: (id) => api.get(`/projects/${id}/trust`),
  getPortfolio: () => api.get('/projects/investor/portfolio'),
};

// Events & Tickets
export const eventsAPI = {
  create: (data) => api.post('/events', data),
  list: (params) => api.get('/events', { params }),
  get: (id) => api.get(`/events/${id}`),
  purchase: (id, data) => api.post(`/events/${id}/purchase`, data),
  myTickets: () => api.get('/events/user/tickets'),
  ticketQR: (code) => `${API_BASE}/events/tickets/${code}/qr`,
  validate: (code, secret) => api.post(`/events/tickets/${code}/validate`, null, { params: { secret } }),
  creatorEvents: () => api.get('/events/creator/events'),
};

// Social
export const socialAPI = {
  follow: (userId) => api.post(`/social/follow/${userId}`),
  unfollow: (userId) => api.delete(`/social/follow/${userId}`),
  following: () => api.get('/social/following'),
  followers: (userId) => api.get(`/social/followers/${userId}`),
  isFollowing: (userId) => api.get(`/social/is-following/${userId}`),
  feed: (limit = 50) => api.get('/social/feed', { params: { limit } }),
  globalFeed: (limit = 30) => api.get('/social/feed/global', { params: { limit } }),
  addComment: (data) => api.post('/social/comments', data),
  getComments: (type, id) => api.get(`/social/comments/${type}/${id}`),
  likeComment: (id) => api.post(`/social/comments/${id}/like`),
  deleteComment: (id) => api.delete(`/social/comments/${id}`),
};

// AI Music Studio
export const studioAPI = {
  generateLyrics: (data) => api.post('/studio/generate-lyrics', data),
  generateAlbumArt: (data) => api.post('/studio/generate-album-art', data),
  generateConcept: (data) => api.post('/studio/generate-concept', data),
  myItems: () => api.get('/studio/my-items'),
  fundingFunnel: (data) => api.post('/studio/funding-funnel', data),
};

// AI Negotiator
export const negotiateAPI = {
  start: (data) => api.post('/studio/negotiate/start', data),
  respond: (negId, data) => api.post(`/studio/negotiate/${negId}/respond`, data),
  myNegotiations: (role = 'buyer') => api.get('/studio/negotiate/my-negotiations', { params: { role } }),
  get: (negId) => api.get(`/studio/negotiate/${negId}`),
  notifications: () => api.get('/studio/notifications'),
  markRead: (id) => api.post(`/studio/notifications/${id}/read`),
};

// Guidance & Orchestration
export const guidanceAPI = {
  watch: (entityType, entityId) => api.post('/guidance/watch', null, { params: { entity_type: entityType, entity_id: entityId } }),
  unwatch: (entityType, entityId) => api.delete('/guidance/watch', { params: { entity_type: entityType, entity_id: entityId } }),
  checkWatching: (entityType, entityId) => api.get('/guidance/watch/check', { params: { entity_type: entityType, entity_id: entityId } }),
  getWatchlist: () => api.get('/guidance/watchlist'),
  getAlerts: (limit = 50) => api.get('/guidance/alerts', { params: { limit } }),
  markAlertRead: (alertId) => api.put(`/guidance/alerts/${alertId}/read`),
  markAllRead: () => api.put('/guidance/alerts/read-all'),
  trackActivity: (activityType, entityType, entityId, label = '') => api.post('/guidance/activity', null, { params: { activity_type: activityType, entity_type: entityType, entity_id: entityId, label } }),
  getRecentActivity: (limit = 6) => api.get('/guidance/recent-activity', { params: { limit } }),
  getNextActions: (contextPage = 'home') => api.get('/guidance/next-actions', { params: { context_page: contextPage } }),
  getOnboarding: () => api.get('/guidance/onboarding'),
  dismissOnboarding: () => api.put('/guidance/onboarding/dismiss'),
  getDashboard: () => api.get('/guidance/dashboard'),
  getBridges: (pageType, entityId) => api.get(`/guidance/bridges/${pageType}/${entityId}`),
  getSmartNotify: (action, entityType, entityId) => api.get(`/guidance/smart-notify/${action}/${entityType}/${entityId}`),
  getNavPulse: () => api.get('/guidance/nav-pulse'),
  getWhisper: (pageType, entityId = '') => api.get(`/guidance/whisper/${pageType}`, { params: entityId ? { entity_id: entityId } : {} }),
  getMomentum: () => api.get('/guidance/momentum'),
  triggerDiscovery: (data) => api.post('/guidance/discover', data),
  recordTouch: (data) => api.post('/guidance/attribution/touch', data),
  getJourney: (entityType, entityId) => api.get(`/guidance/attribution/journey/${entityType}/${entityId}`),
  getAttributionOverview: () => api.get('/guidance/attribution/overview'),
};

// Artist Profiles
export const artistsAPI = {
  getProfile: (userId) => api.get(`/artists/${userId}`),
  updateProfile: (data) => api.put('/artists/profile', data),
  revenueDashboard: () => api.get('/artists/revenue/dashboard'),
  requestPayout: (data) => api.post('/artists/revenue/payout', data),
  localScene: (location) => api.get(`/artists/local-scene/${encodeURIComponent(location)}`),
};

// Community (Promoter/Curator/Heatmaps/Leaderboard)
export const communityAPI = {
  applyRole: (data) => api.post('/community/apply-role', data),
  boost: (data) => api.post('/community/boost', data),
  createCollection: (data) => api.post('/community/collections', data),
  getCollections: () => api.get('/community/collections'),
  cityHeatmap: (city) => api.get(`/community/heatmap/${encodeURIComponent(city)}`),
  multiCityHeatmap: () => api.get('/community/heatmap'),
  backerLeaderboard: (projectId) => api.get(`/community/leaderboard/${projectId}`),
};

// Marketing Engine
export const marketingAPI = {
  // Campaigns
  createCampaign: (data) => api.post('/marketing/campaigns', data),
  listCampaigns: () => api.get('/marketing/campaigns'),
  getCampaign: (id) => api.get(`/marketing/campaigns/${id}`),
  generatePlan: (id) => api.post(`/marketing/campaigns/${id}/generate-plan`),
  activateCampaign: (id) => api.put(`/marketing/campaigns/${id}/activate`),
  boostCampaign: (id, data) => api.post(`/marketing/campaigns/${id}/boost`, data),
  addContent: (id, data) => api.post(`/marketing/campaigns/${id}/content`, data),
  // Smart Links
  getSmartLink: (slug) => api.get(`/marketing/smartlink/${slug}`),
  setupSmartLink: (data) => api.put('/marketing/smartlink/setup', data),
  getPressKit: (userId) => api.get(`/marketing/presskit/${userId}`),
  // Email
  sendEmail: (data) => api.post('/marketing/email/send', data),
  emailHistory: () => api.get('/marketing/email/history'),
  createAutomation: (data) => api.post('/marketing/automations', data),
  listAutomations: () => api.get('/marketing/automations'),
  toggleAutomation: (id) => api.put(`/marketing/automations/${id}/toggle`),
  // Promoter
  createBrief: (data) => api.post('/marketing/promo-briefs', data),
  listBriefs: (status) => api.get('/marketing/promo-briefs', { params: { status } }),
  applyToBrief: (id, data) => api.post(`/marketing/promo-briefs/${id}/apply`, data),
  promoterStats: () => api.get('/marketing/promo/my-stats'),
  // Analytics
  analyticsOverview: () => api.get('/marketing/analytics/overview'),
  analyticsFunnel: (type, id) => api.get(`/marketing/analytics/funnel/${type}/${id}`),
  // Landing Pages
  createLandingPage: (data) => api.post('/marketing/landing-pages', data),
  listLandingPages: () => api.get('/marketing/landing-pages'),
  getLandingPage: (id) => api.get(`/marketing/landing-pages/${id}`),
  updateLandingPage: (id, data) => api.put(`/marketing/landing-pages/${id}`, data),
  publishLandingPage: (id) => api.put(`/marketing/landing-pages/${id}/publish`),
  generateLandingPage: (id) => api.post(`/marketing/landing-pages/${id}/generate`),
  viewLandingPage: (slug) => api.get(`/marketing/lp/${slug}`),
  trackLandingClick: (slug) => api.post(`/marketing/lp/${slug}/click`),
  // Scheduled Content
  scheduleContent: (data) => api.post('/marketing/scheduled-content', data),
  listScheduled: () => api.get('/marketing/scheduled-content'),
  cancelScheduled: (id) => api.delete(`/marketing/scheduled-content/${id}`),
  // A/B Testing
  createABTest: (data) => api.post('/marketing/ab-test', data),
  listABTests: () => api.get('/marketing/ab-tests'),
  runABTest: (id) => api.post(`/marketing/ab-test/${id}/run`),
  aiSuggestSubjects: (id, data) => api.post(`/marketing/ab-test/${id}/ai-suggest`, data),
  // Social Cross-Posting
  generateSocialPosts: (data) => api.post('/marketing/social-post/generate', data),
  listSocialPosts: () => api.get('/marketing/social-posts'),
  trackCopy: (id, data) => api.post(`/marketing/social-post/${id}/copy-track`, data),
  // Stripe Boosts
  boostStripe: (id, data) => api.post(`/marketing/campaigns/${id}/boost-stripe`, data),
  // Creator Streaks
  creatorStreaks: () => api.get('/marketing/creator-streaks'),
};

// ═══════════════════════ MARKETING SUITE (Private) ═══════════════════════
export const suiteAPI = {
  checkAccess: () => api.get('/suite/access-check'),
  dashboard: () => api.get('/suite/dashboard'),
  // Team
  listTeam: () => api.get('/suite/team'),
  addTeamMember: (data) => api.post('/suite/team/add', data),
  removeTeamMember: (data) => api.post('/suite/team/remove', data),
  // Projects
  createProject: (data) => api.post('/suite/projects', data),
  listProjects: () => api.get('/suite/projects'),
  getProject: (id) => api.get(`/suite/projects/${id}`),
  updateProject: (id, data) => api.put(`/suite/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/suite/projects/${id}`),
  // Video Ads
  generateVideoAd: (data) => api.post('/suite/video-ads/generate', data),
  listVideoAds: (projectId) => api.get('/suite/video-ads', { params: projectId ? { project_id: projectId } : {} }),
  getVideoAd: (id) => api.get(`/suite/video-ads/${id}`),
  deleteVideoAd: (id) => api.delete(`/suite/video-ads/${id}`),
  videoAdFileUrl: (jobId) => `${BACKEND_URL}/api/video/file/${jobId}`,
  // AI Copy
  generateCopy: (data) => api.post('/suite/ai-copy/generate', data),
  listCopy: (projectId) => api.get('/suite/ai-copy', { params: projectId ? { project_id: projectId } : {} }),
  deleteCopy: (id) => api.delete(`/suite/ai-copy/${id}`),
  // Brand Kit
  getBrandKit: () => api.get('/suite/brand-kit'),
  updateBrandKit: (data) => api.put('/suite/brand-kit', data),
  // Assets
  saveAsset: (data) => api.post('/suite/assets', data),
  listAssets: (type, projectId) => api.get('/suite/assets', { params: { ...(type && { asset_type: type }), ...(projectId && { project_id: projectId }) } }),
  deleteAsset: (id) => api.delete(`/suite/assets/${id}`),
  // Calendar
  createCalendarEntry: (data) => api.post('/suite/calendar', data),
  listCalendar: (month) => api.get('/suite/calendar', { params: month ? { month } : {} }),
  updateCalendarEntry: (id, data) => api.put(`/suite/calendar/${id}`, data),
  deleteCalendarEntry: (id) => api.delete(`/suite/calendar/${id}`),

  // ── Command center & analytics
  commandCenter: () => api.get('/suite/command-center'),
  analyticsOverview: (days = 30) => api.get('/suite/analytics/overview', { params: { days } }),
  campaignAnalytics: (id) => api.get(`/suite/analytics/campaign/${id}`),
  aiInsights: (data) => api.post('/suite/analytics/ai-insights', data),
  insightsHistory: () => api.get('/suite/analytics/insights-history'),
  reportData: (id) => api.get(`/suite/reports/campaign/${id}`),
  exportCsvUrl: (kind, campaignId) =>
    `${BACKEND_URL}/api/suite/reports/export?kind=${kind}${campaignId ? `&campaign_id=${campaignId}` : ''}`,

  // ── Campaigns / tasks / strategy
  createCampaign: (data) => api.post('/suite/campaigns', data),
  listCampaigns: (params) => api.get('/suite/campaigns', { params }),
  getCampaign: (id) => api.get(`/suite/campaigns/${id}`),
  updateCampaign: (id, data) => api.put(`/suite/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/suite/campaigns/${id}`),
  aiCampaignPlan: (id, data) => api.post(`/suite/campaigns/${id}/ai-plan`, data),
  logSpend: (id, data) => api.post(`/suite/campaigns/${id}/spend`, data),
  listSpend: (id) => api.get(`/suite/campaigns/${id}/spend`),
  createTask: (data) => api.post('/suite/tasks', data),
  listTasks: (params) => api.get('/suite/tasks', { params }),
  updateTask: (id, data) => api.put(`/suite/tasks/${id}`, data),
  deleteTask: (id) => api.delete(`/suite/tasks/${id}`),

  // ── Personas / briefs / competitors
  createPersona: (data) => api.post('/suite/personas', data),
  aiPersonas: (data) => api.post('/suite/personas/ai-generate', data),
  listPersonas: () => api.get('/suite/personas'),
  updatePersona: (id, data) => api.put(`/suite/personas/${id}`, data),
  deletePersona: (id) => api.delete(`/suite/personas/${id}`),
  createBrief: (data) => api.post('/suite/briefs', data),
  aiBrief: (data) => api.post('/suite/briefs/ai-generate', data),
  listBriefs: (params) => api.get('/suite/briefs', { params }),
  deleteBrief: (id) => api.delete(`/suite/briefs/${id}`),
  addCompetitor: (data) => api.post('/suite/competitors', data),
  listCompetitors: () => api.get('/suite/competitors'),
  deleteCompetitor: (id) => api.delete(`/suite/competitors/${id}`),
  aiCompetitiveAnalysis: (data) => api.post('/suite/competitors/ai-analyze', data),
  listAnalysis: () => api.get('/suite/analysis'),

  // ── Approvals & activity
  requestApproval: (data) => api.post('/suite/approvals', data),
  listApprovals: (status) => api.get('/suite/approvals', { params: status ? { status } : {} }),
  decideApproval: (id, data) => api.put(`/suite/approvals/${id}`, data),
  activityFeed: () => api.get('/suite/activity'),

  // ── QR codes
  createQR: (data) => api.post('/suite/qr', data),
  listQR: (campaignId) => api.get('/suite/qr', { params: campaignId ? { campaign_id: campaignId } : {} }),
  qrAnalytics: (id) => api.get(`/suite/qr/${id}/analytics`),
  deleteQR: (id) => api.delete(`/suite/qr/${id}`),
  qrRenderUrl: (id, fmt = 'png', scale = 12) => `${BACKEND_URL}/api/suite/qr/${id}/render?fmt=${fmt}&scale=${scale}`,

  // ── Smart links
  createLink: (data) => api.post('/suite/links', data),
  listLinks: (campaignId) => api.get('/suite/links', { params: campaignId ? { campaign_id: campaignId } : {} }),
  linkAnalytics: (id) => api.get(`/suite/links/${id}/analytics`),
  deleteLink: (id) => api.delete(`/suite/links/${id}`),

  // ── Design studio
  designPresets: () => api.get('/suite/designs/presets'),
  saveDesign: (data) => api.post('/suite/designs', data),
  listDesigns: () => api.get('/suite/designs'),
  getDesign: (id) => api.get(`/suite/designs/${id}`),
  deleteDesign: (id) => api.delete(`/suite/designs/${id}`),
  exportDesign: (id, data) => api.post(`/suite/designs/${id}/export`, data),
  aiDesignLayout: (data) => api.post('/suite/designs/ai-layout', data),
  importDesign: (formData) => api.post('/suite/designs/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  aiImage: (data) => api.post('/suite/ai/image', data),

  // ── Asset library
  uploadAsset: (formData) => api.post('/suite/uploads', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  assetLibrary: (params) => api.get('/suite/assets/library', { params }),
  fileUrl: (assetId) => `${BACKEND_URL}/api/suite/file/${assetId}`,

  // ── Audience
  createList: (data) => api.post('/suite/lists', data),
  getLists: () => api.get('/suite/lists'),
  deleteList: (id) => api.delete(`/suite/lists/${id}`),
  addContact: (data) => api.post('/suite/contacts', data),
  importContacts: (formData, listId) => api.post(`/suite/contacts/import${listId ? `?list_id=${listId}` : ''}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  listContacts: (params) => api.get('/suite/contacts', { params }),
  updateContact: (id, data) => api.put(`/suite/contacts/${id}`, data),
  deleteContact: (id) => api.delete(`/suite/contacts/${id}`),

  // ── Email
  saveEmail: (data) => api.post('/suite/email-campaigns', data),
  listEmails: (campaignId) => api.get('/suite/email-campaigns', { params: campaignId ? { campaign_id: campaignId } : {} }),
  getEmail: (id) => api.get(`/suite/email-campaigns/${id}`),
  deleteEmail: (id) => api.delete(`/suite/email-campaigns/${id}`),
  aiEmail: (data) => api.post('/suite/email-campaigns/ai-generate', data),
  testEmail: (id, data) => api.post(`/suite/email-campaigns/${id}/test`, data),
  sendEmail: (id, data) => api.post(`/suite/email-campaigns/${id}/send`, data),
  emailStats: (id) => api.get(`/suite/email-campaigns/${id}/stats`),

  // ── SMS
  createSms: (data) => api.post('/suite/sms-campaigns', data),
  listSms: () => api.get('/suite/sms-campaigns'),
  deleteSms: (id) => api.delete(`/suite/sms-campaigns/${id}`),
  sendSms: (id, data) => api.post(`/suite/sms-campaigns/${id}/send`, data),
  smsStats: (id) => api.get(`/suite/sms-campaigns/${id}/stats`),

  // ── Social
  createSocialPost: (data) => api.post('/suite/social', data),
  aiSocial: (data) => api.post('/suite/social/ai-generate', data),
  listSocial: (params) => api.get('/suite/social', { params }),
  updateSocial: (id, data) => api.put(`/suite/social/${id}`, data),
  deleteSocial: (id) => api.delete(`/suite/social/${id}`),

  // ── Landing pages & leads
  savePage: (data) => api.post('/suite/pages', data),
  aiPage: (data) => api.post('/suite/pages/ai-generate', data),
  listPages: () => api.get('/suite/pages'),
  getPage: (id) => api.get(`/suite/pages/${id}`),
  deletePage: (id) => api.delete(`/suite/pages/${id}`),
  publicPage: (slug) => api.get(`/suite/p/${slug}`),
  submitLead: (slug, data) => api.post(`/suite/p/${slug}/lead`, data),
  listLeads: (params) => api.get('/suite/leads', { params }),
  updateLead: (id, data) => api.put(`/suite/leads/${id}`, data),
  deleteLead: (id) => api.delete(`/suite/leads/${id}`),

  // ── Automation
  saveSequence: (data) => api.post('/suite/sequences', data),
  aiSequence: (data) => api.post('/suite/sequences/ai-generate', data),
  listSequences: () => api.get('/suite/sequences'),
  deleteSequence: (id) => api.delete(`/suite/sequences/${id}`),
  enrollSequence: (id, data) => api.post(`/suite/sequences/${id}/enroll`, data),
  sequenceEnrollments: (id) => api.get(`/suite/sequences/${id}/enrollments`),
  processAutomations: () => api.post('/suite/automation/process'),

  // ── Integrations
  getIntegrations: () => api.get('/suite/integrations'),
  updateIntegrations: (data) => api.put('/suite/integrations', data),

  // ── Social publishing, share links, print packs
  socialAccounts: () => api.get('/suite/social/accounts'),
  saveSocialAccounts: (data) => api.put('/suite/social/accounts', data),
  publishSocial: (id) => api.post(`/suite/social/${id}/publish`),
  setPublicBase: (baseUrl) => api.put('/suite/public-base', { base_url: baseUrl }),
  createShare: (data) => api.post('/suite/shares', data),
  listShares: () => api.get('/suite/shares'),
  deleteShare: (id) => api.delete(`/suite/shares/${id}`),
  sharedReport: (token) => api.get(`/suite/shared/${token}`),
  printPack: (designId, data) => api.post(`/suite/designs/${designId}/print-pack`, data, { responseType: 'blob' }),
};

export default api;
